import React, { useState, useEffect, useRef, useMemo } from 'react';
import { formatBrandMoney, getBrandLocale } from '../../shared/utils/brandCurrency';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemTableCheckbox from '../../products/saudi/components/QawaemTableCheckbox';
import { BookOpen, Search, Plus, Edit, Trash2, RefreshCcw, ShoppingCart, AlertTriangle, AlertCircle, CheckCircle, Calendar, ChevronDown, ChevronUp, Upload, Download, FileSpreadsheet, FileOutput, Filter, Eye, X, XCircle, LayoutGrid, Shield, Layers, Gauge, FileText, Clock, List, LayoutList, Check, PauseCircle, Package, DollarSign, User, Receipt, CreditCard } from 'lucide-react';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import useColumnHeaderFilters from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import { buildExportFilename } from '../../shared/utils/exportFilename';
import localApiAdapter from '../../core/services/localApiAdapter';
import { exportToCSV, exportToExcel, triggerFileInput, importFromFile } from '../../shared/utils/importExportUtils';
import { generateReportPDF } from '../../shared/utils/reportPdfGenerator';
import { generateReportExcel } from '../../shared/utils/reportExcelGeneratorAdvanced';
import { CustomSelect, CustomDatePicker, CustomNumberInput } from '../../shared/components/custom';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import { makePageT } from '../../shared/utils/pageI18n';
import VentesListPageChrome, { facturationTableScrollClass } from './components/VentesListPageChrome';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import {
  mapVentesStatCards,
  useCrudModalBodyLock,
  initVentesColumnVisibility,
  buildVentesQawaemColumnDefs,
  isVentesColumnVisible,
  buildVentesBulkDeleteFilterAction,
  computeVentesRowSelection,
  resolveVentesEntityReference,
  useVentesEntityReference,
  VENTES_REF_INPUT_CLASS,
  VentesConfigNotice,
  sliceVentesListItems,
  matchesVentesListDateRange,
} from './ventesListPageUtils';
import { useVentesConfig } from './useVentesConfig';
import { createCommissionForSale } from './ventesCrossModuleUtils';
import { useSettings } from '../../core/contexts/SettingsContext';
import { useSaudiListDateRange } from '../../products/saudi/hooks/useSaudiListDateRange';
import QawaemErpCrudModal, { QawaemErpViewModal } from '../../products/saudi/components/QawaemErpCrudModal';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import { mergeStickySelectClass } from '../../products/saudi/components/QawaemTableStickyActions';

const LivreDesVentesPage = () => {
  const { t, i18n } = useTranslation();
  const p = makePageT(t, 'modules.ventes.pages.livreDesVentesPage');
  const { isDashboardLightMode } = useGlobalTheme();
  const qawaem = isSaudiBrand();
  const dateLocale = getBrandLocale(i18n.language);
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString(dateLocale) : '-';

  const getPaymentStatusLabel = (status) => {
    const map = {
      pending: p('status_pending'),
      partial: p('status_partial'),
      paid: p('status_paid'),
      cancelled: p('status_cancelled'),
    };
    return map[status] || status || p('em_dash');
  };

  const getPaymentMethodLabel = (method) => {
    const map = {
      cash: p('method_cash'),
      check: p('method_check'),
      transfer: p('method_transfer'),
      mada: p('method_mada'),
      card: p('method_card'),
      credit: p('method_credit'),
    };
    return map[method] || method || p('na');
  };

  const paymentMethodOptions = useMemo(
    () => [
      { value: 'cash', label: p('method_cash') },
      { value: 'check', label: p('method_check') },
      { value: 'transfer', label: p('method_transfer') },
      { value: 'mada', label: p('method_mada') },
      { value: 'card', label: p('method_card') },
      { value: 'credit', label: p('method_credit') },
    ],
    [t],
  );

  // Close dropdowns on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isClickOnStatusButton = event.target.closest('[data-sale-status-dropdown-button]');
      const isClickInStatusDropdown = event.target.closest('[data-sale-status-dropdown]');
      if (!isClickOnStatusButton && !isClickInStatusDropdown) {
        setShowStatusDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const [searchTerm, setSearchTerm] = useState('');
  const [sales, setSales] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [viewMode, setViewMode] = useState('table');
  const ventesConfig = useVentesConfig();
  const { dateRange } = useSaudiListDateRange();

  useEffect(() => {
    setViewMode(ventesConfig.defaultView || 'table');
  }, [ventesConfig.defaultView]);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);

  useCrudModalBodyLock(showModal, showViewModal);
  const { settings } = useSettings();
  const generateSaleRef = useVentesEntityReference('sale', sales);
  
  const [formTab, setFormTab] = useState('general');
  
  const [formData, setFormData] = useState({ 
    sale_number: '',
    customer_id: '', 
    customer_name: '',
    customer_code: '',
    sale_date: new Date().toISOString().split('T')[0],
    invoice_number: '',
    invoice_date: null,
    due_date: null,
    total_amount: '',
    paid_amount: '',
    remaining_amount: '',
    payment_status: 'pending',
    payment_method: '',
    warehouse_id: '',
    warehouse_name: '',
    product_count: '',
    tax_amount: '',
    discount_amount: '',
    notes: ''
  });
  
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_sales');
  const [quickFilter, setQuickFilter] = useState('all');
  
  // PDF and Excel export states
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isGeneratingExcel, setIsGeneratingExcel] = useState(false);
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  const [viewType, setViewType] = useState('manual'); // 'manual' or 'converted'
  
  // Dropdown data states
  const [availableCustomers, setAvailableCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [availableWarehouses, setAvailableWarehouses] = useState([]);
  const [loadingWarehouses, setLoadingWarehouses] = useState(false);
  
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const statusDropdownRef = useRef(null);
  
  const saleFormTabs = useMemo(() => ([
    { id: 'general', label: p('section_identification'), icon: ShoppingCart, iconColor: 'text-blue-400' },
    { id: 'amounts', label: p('section_amounts'), icon: DollarSign, iconColor: 'text-purple-400' },
    { id: 'notes', label: p('section_notes'), icon: FileText, iconColor: 'text-red-400' },
  ]), [p]);

  const { filters, setFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const thFilterClass = 'text-white font-semibold py-4';

  const tableColumnDefs = useMemo(
    () => [
      { key: 'sale_number', label: p('col_sale_number'), required: true },
      { key: 'customer_name', label: p('col_customer') },
      { key: 'sale_date', label: p('col_date') },
      { key: 'total_amount', label: p('col_amount') },
      { key: 'payment_method', label: p('col_payment') },
      { key: 'payment_status', label: p('status_label') },
    ],
    [p],
  );
  const [columnVisibility, setColumnVisibility] = useState(() =>
    initVentesColumnVisibility(tableColumnDefs.map((c) => c.key)),
  );
  const qawaemColumnDefs = useMemo(
    () => buildVentesQawaemColumnDefs(tableColumnDefs, columnVisibility),
    [tableColumnDefs, columnVisibility],
  );
  const handleToggleColumn = (key) => {
    setColumnVisibility((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  const qawaemVisibleColumnCount = qawaemColumnDefs.filter((c) => c.checked !== false).length;
  const showCol = (key) => isVentesColumnVisible(columnVisibility, key);
  const tableColSpan = tableColumnDefs.filter((c) => showCol(c.key)).length + 2;

  useEffect(() => { 
    loadSales(); 
    loadCustomers();
    loadWarehouses();
  }, []);
  
  useEffect(() => {
    loadSales();
  }, [viewType]);
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadSales = async () => {
    try {
      const response = await localApiAdapter.get(`/ventes/livre-des-ventes?source_type=${viewType}`);
      const rows = response?.data || response || [];
      setSales(sortRecordsNewestFirst(Array.isArray(rows) ? rows : []));
    } catch (error) {
      console.error('Error loading sales:', error);
      setSales([]);
    }
  };
  
  const loadCustomers = async () => {
    try {
      setLoadingCustomers(true);
      const response = await localApiAdapter.get('/facturation/clients');
      const rows = response?.data || response?.clients || response || [];
      setAvailableCustomers(Array.isArray(rows) ? rows : []);
    } catch (error) {
      console.error('Error loading customers:', error);
      setAvailableCustomers([]);
    } finally {
      setLoadingCustomers(false);
    }
  };
  
  const loadWarehouses = async () => {
    try {
      setLoadingWarehouses(true);
      const response = await localApiAdapter.get('/inventory/warehouses');
      setAvailableWarehouses(response?.data || response || []);
    } catch (error) {
      console.error('Error loading warehouses:', error);
      setAvailableWarehouses([]);
    } finally {
      setLoadingWarehouses(false);
    }
  };

  const stats = useMemo(() => ({
    total: sales.length,
    pending: sales.filter(s => s.payment_status === 'pending').length,
    partial: sales.filter(s => s.payment_status === 'partial').length,
    paid: sales.filter(s => s.payment_status === 'paid').length,
    overdue: sales.filter(s => s.due_date && new Date(s.due_date) < new Date() && s.payment_status !== 'paid').length,
    totalAmount: sales.reduce((sum, s) => sum + (parseFloat(s.total_amount) || 0), 0),
    totalRemaining: sales.reduce((sum, s) => sum + (parseFloat(s.remaining_amount) || 0), 0),
  }), [sales]);

  const mainTabs = useMemo(() => [
    { id: 'all_sales', name: p('tab_general'), icon: LayoutGrid },
    { id: 'par_paiement', name: p('tab_by_payment'), icon: CreditCard },
    { id: 'par_client', name: p('tab_by_customer'), icon: User },
  ], [t]);

  const quickFilterButtons = useMemo(() => [
    { id: 'all', name: p('filter_all'), icon: Layers },
    { id: 'pending', name: p('quick_pending'), icon: Clock },
    { id: 'partial', name: p('quick_partial'), icon: AlertCircle },
    { id: 'paid', name: p('quick_paid'), icon: CheckCircle },
  ], [t]);

  const salesDistribution = useMemo(() => ({
    all_sales: {
      title: p('dist_overview'),
      data: [
        { filterKey: 'pending', name: p('dist_pending'), count: stats.pending, color: 'bg-gradient-to-r from-orange-500 to-orange-600' },
        { filterKey: 'partial', name: p('dist_partial'), count: stats.partial, color: 'bg-gradient-to-r from-yellow-500 to-yellow-600' },
        { filterKey: 'paid', name: p('dist_paid'), count: stats.paid, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { filterKey: 'overdue', name: p('dist_overdue'), count: stats.overdue, color: 'bg-gradient-to-r from-red-500 to-red-600' },
      ],
    },
    par_paiement: {
      title: p('dist_by_payment'),
      data: [
        { filterKey: 'unpaid', name: p('dist_unpaid'), count: stats.pending, color: 'bg-gradient-to-r from-red-500 to-red-600' },
        { filterKey: 'partial', name: p('dist_partials'), count: stats.partial, color: 'bg-gradient-to-r from-yellow-500 to-yellow-600' },
        { filterKey: 'complete', name: p('dist_complete'), count: stats.paid, color: 'bg-gradient-to-r from-green-500 to-green-600' },
      ],
    },
    par_client: {
      title: p('dist_by_customer'),
      data: (() => {
        const customerCounts = {};
        sales.forEach(s => {
          const customer = s.customer_name || p('dist_unspecified');
          customerCounts[customer] = (customerCounts[customer] || 0) + 1;
        });
        const topCustomers = Object.entries(customerCounts)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 4);
        const colors = [
          'bg-gradient-to-r from-blue-500 to-blue-600',
          'bg-gradient-to-r from-purple-500 to-purple-600',
          'bg-gradient-to-r from-pink-500 to-pink-600',
          'bg-gradient-to-r from-indigo-500 to-indigo-600',
        ];
        return topCustomers.map(([name, count], i) => ({ filterKey: name, name, count, color: colors[i] }));
      })(),
    },
  }), [sales, stats, t]);

  const distributionInfo = salesDistribution[mainViewMode] || salesDistribution.all_sales;

  const paymentStatusFilterOptions = useMemo(() => ([
    { value: 'pending', label: p('status_pending') },
    { value: 'partial', label: p('status_partial') },
    { value: 'paid', label: p('status_paid') },
    { value: 'cancelled', label: p('status_cancelled') },
  ]), [p]);

  const columnFilterRules = useMemo(() => ([
    textColumnRule('sale_number'),
    textColumnRule('customer_name'),
    textColumnRule('sale_date', (r) => (r.sale_date ? fmtDate(r.sale_date) : '')),
    textColumnRule('total_amount', (r) => String(r.total_amount ?? '')),
    textColumnRule('payment_method', (r) => getPaymentMethodLabel(r.payment_method)),
    exactColumnRule('payment_status', (r) => r.payment_status ?? ''),
  ]), [p]);

  const baseFilteredSales = useMemo(() => sales.filter(s => {
    if (!matchesVentesListDateRange(s.sale_date, dateRange)) return false;
    
    if (quickFilter !== 'all' && s.payment_status !== quickFilter) return false;
    
    if (searchTerm && !s.sale_number?.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !s.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !s.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    
    if (activeDistributionFilter) {
      switch(mainViewMode) {
        case 'all_sales':
          if (activeDistributionFilter === 'pending' && s.payment_status !== 'pending') return false;
          if (activeDistributionFilter === 'partial' && s.payment_status !== 'partial') return false;
          if (activeDistributionFilter === 'paid' && s.payment_status !== 'paid') return false;
          if (activeDistributionFilter === 'overdue' && (s.payment_status === 'paid' || !s.due_date || new Date(s.due_date) >= new Date())) return false;
          break;
        case 'par_paiement':
          if (activeDistributionFilter === 'unpaid' && s.payment_status !== 'pending') return false;
          if (activeDistributionFilter === 'partial' && s.payment_status !== 'partial') return false;
          if (activeDistributionFilter === 'complete' && s.payment_status !== 'paid') return false;
          break;
        case 'par_client':
          if (activeDistributionFilter && s.customer_name !== activeDistributionFilter) return false;
          break;
      }
    }
    
    return true;
  }), [sales, quickFilter, searchTerm, activeDistributionFilter, mainViewMode, dateRange]);

  const filteredSales = useMemo(
    () => applyColumnFilters(baseFilteredSales, filters, columnFilterRules),
    [baseFilteredSales, filters, columnFilterRules],
  );

  const filteredSaleIds = useMemo(() => filteredSales.map((r) => r.id), [filteredSales]);
  const displayedSales = useMemo(
    () => sliceVentesListItems(filteredSales, ventesConfig.itemsPerPage),
    [filteredSales, ventesConfig.itemsPerPage],
  );
  const { allRowsSelected, someRowsSelected } = useMemo(
    () => computeVentesRowSelection(filteredSaleIds, selectedRecords),
    [filteredSaleIds, selectedRecords],
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const saleNumber = resolveVentesEntityReference(
        settings,
        'sale',
        sales,
        formData.sale_number,
      );
      const submitData = { 
        sale_number: saleNumber,
        customer_id: parseInt(formData.customer_id) || null,
        customer_name: formData.customer_name,
        customer_code: formData.customer_code,
        sale_date: formData.sale_date ? new Date(formData.sale_date).toISOString() : null,
        invoice_number: formData.invoice_number,
        invoice_date: formData.invoice_date ? new Date(formData.invoice_date).toISOString() : null,
        due_date: formData.due_date ? new Date(formData.due_date).toISOString() : null,
        total_amount: parseFloat(formData.total_amount) || 0,
        paid_amount: parseFloat(formData.paid_amount) || 0,
        remaining_amount: parseFloat(formData.remaining_amount) || 0,
        payment_status: formData.payment_status,
        payment_method: formData.payment_method,
        warehouse_id: parseInt(formData.warehouse_id) || null,
        warehouse_name: formData.warehouse_name,
        product_count: parseInt(formData.product_count) || 0,
        tax_amount: parseFloat(formData.tax_amount) || 0,
        discount_amount: parseFloat(formData.discount_amount) || 0,
        notes: formData.notes || ''
      };
      
      if (editingRecord) {
        await localApiAdapter.put(`/ventes/livre-des-ventes/${editingRecord.id}`, submitData);
        notifyUpdated(showNotify, p('entity_label', { defaultValue: 'عملية بيع' }));
      } else {
        await localApiAdapter.post('/ventes/livre-des-ventes', submitData);
        await createCommissionForSale(
          localApiAdapter,
          settings,
          ventesConfig,
          { ...submitData, sale_number: saleNumber },
        );
        notifyCreated(showNotify, p('entity_label', { defaultValue: 'عملية بيع' }));
      }
    } catch (error) {
      console.error('Error saving:', error);
      notifyError(showNotify, p('error_save', { message: error.message || p('error_unknown') }));
      return;
    } finally {
      resetForm();
      loadSales();
    }
  };

  const handleEdit = (record) => {
    setFormTab('general');
    setEditingRecord(record);
    setFormData({ 
      sale_number: record.sale_number || '',
      customer_id: record.customer_id?.toString() || '',
      customer_name: record.customer_name || '',
      customer_code: record.customer_code || '',
      sale_date: record.sale_date ? new Date(record.sale_date) : null,
      invoice_number: record.invoice_number || '',
      invoice_date: record.invoice_date ? new Date(record.invoice_date) : null,
      due_date: record.due_date ? new Date(record.due_date) : null,
      total_amount: record.total_amount?.toString() || '',
      paid_amount: record.paid_amount?.toString() || '',
      remaining_amount: record.remaining_amount?.toString() || '',
      payment_status: record.payment_status || 'pending',
      payment_method: record.payment_method || '',
      warehouse_id: record.warehouse_id?.toString() || '',
      warehouse_name: record.warehouse_name || '',
      product_count: record.product_count?.toString() || '',
      tax_amount: record.tax_amount?.toString() || '',
      discount_amount: record.discount_amount?.toString() || '',
      notes: record.notes || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('delete_confirm'))) {
      try {
        await localApiAdapter.delete(`/ventes/livre-des-ventes/${id}`);
        notifyDeleted(showNotify, p('entity_label', { defaultValue: 'عملية بيع' }));
        loadSales();
      } catch (error) {
        console.error('Error:', error);
        notifyError(showNotify, p('error_delete', { defaultValue: 'تعذّر حذف عملية البيع' }));
      }
    }
  };

  const handleBulkDelete = async () => {
    if (window.confirm(p('bulk_delete_confirm', { count: selectedRecords.length }))) {
      try {
        const deleteCount = selectedRecords.length;
        await Promise.all(selectedRecords.map(id => localApiAdapter.delete(`/ventes/livre-des-ventes/${id}`)));
        notifyBulkDeleted(showNotify, deleteCount, p('entity_label_plural', { defaultValue: 'عملية بيع' }));
        setSelectedRecords([]);
        loadSales();
      } catch (error) {
        console.error('Error deleting records:', error);
        notifyError(showNotify, p('error_bulk_delete', { defaultValue: 'تعذّر حذف عمليات البيع المحددة' }));
      }
    }
  };

  const openCreateSaleModal = () => {
    if (!ventesConfig.enableSalesLedger) return;
    setEditingRecord(null);
    setFormTab('general');
    setFormData({
      sale_number: ventesConfig.autoGenerateSaleReference !== false ? generateSaleRef() : '',
      customer_id: '',
      customer_name: '',
      customer_code: '',
      sale_date: new Date().toISOString().split('T')[0],
      invoice_number: '',
      invoice_date: null,
      due_date: null,
      total_amount: '',
      paid_amount: '',
      remaining_amount: '',
      payment_status: ventesConfig.defaultPaymentStatus || 'pending',
      payment_method: '',
      warehouse_id: '',
      warehouse_name: '',
      product_count: '',
      tax_amount: '',
      discount_amount: '',
      notes: '',
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({ 
      sale_number: '',
      customer_id: '', 
      customer_name: '',
      customer_code: '',
      sale_date: new Date().toISOString().split('T')[0],
      invoice_number: '',
      invoice_date: null,
      due_date: null,
      total_amount: '',
      paid_amount: '',
      remaining_amount: '',
      payment_status: 'pending',
      payment_method: '',
      warehouse_id: '',
      warehouse_name: '',
      product_count: '',
      tax_amount: '',
      discount_amount: '',
      notes: ''
    });
    setEditingRecord(null);
    setFormTab('general');
    setShowModal(false);
  };

  const handleSelectAll = () => {
    if (allRowsSelected || someRowsSelected) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(filteredSaleIds);
    }
  };
  const handleSelectRecord = (id) => { setSelectedRecords(selectedRecords.includes(id) ? selectedRecords.filter(sId => sId !== id) : [...selectedRecords, id]); };

  // Export handlers
  const handleExport = () => {
    exportToCSV(filteredSales, [
      { key: 'sale_number', label: p('col_sale_number') },
      { key: 'customer_name', label: p('col_customer') },
      { key: 'total_amount', label: p('col_total_amount') },
      { key: 'payment_status', label: p('col_payment_status') }
    ], buildExportFilename('common.export_files.filename_prefix.sales_ledger', { ext: 'csv' }));
  };

  // Export PDF
  const handleExportPDF = async () => {
    try {
      setIsGeneratingPDF(true);
      
      const filteredData = filteredSales;
      
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = `
        <div class="report-content">
          <table>
            <thead>
              <tr>
                <th>${p('col_sale_number')}</th>
                <th>${p('col_customer')}</th>
                <th>${p('col_date')}</th>
                <th>${p('col_total_amount')}</th>
                <th>${p('col_paid')}</th>
                <th>${p('col_remaining')}</th>
                <th>${p('status_label')}</th>
              </tr>
            </thead>
            <tbody>
              ${filteredData.map(item => `
                <tr>
                  <td>${item.sale_number || 'N/A'}</td>
                  <td>${item.customer_name || 'N/A'}</td>
                  <td>${fmtDate(item.sale_date)}</td>
                  <td>${formatBrandMoney(item.total_amount || 0)}</td>
                  <td>${formatBrandMoney(item.paid_amount || 0)}</td>
                  <td>${formatBrandMoney(item.remaining_amount || 0)}</td>
                  <td>${item.payment_status || 'N/A'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      `;
      
      document.body.appendChild(tempDiv);
      await generateReportPDF(p('report_title'), {});
      document.body.removeChild(tempDiv);
      
    } catch (error) {
      console.error('PDF export error:', error);
      alert(p('error_pdf'));
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // Export Excel (Advanced)
  const handleExportExcel = async () => {
    try {
      setIsGeneratingExcel(true);
      
      const filteredData = filteredSales;
      
      const columns = [
        { key: 'sale_number', label: p('col_sale_number'), width: 20 },
        { key: 'customer_name', label: p('col_customer'), width: 25 },
        { key: 'invoice_number', label: p('col_invoice_number'), width: 20 },
        { key: 'sale_date', label: p('col_sale_date'), width: 15 },
        { key: 'total_amount', label: p('col_total_amount'), width: 15 },
        { key: 'paid_amount', label: p('col_paid'), width: 15 },
        { key: 'remaining_amount', label: p('col_remaining'), width: 15 },
        { key: 'payment_status', label: p('status_label'), width: 12 }
      ];
      
      const data = filteredData.map(item => ({
        sale_number: item.sale_number || '',
        customer_name: item.customer_name || '',
        invoice_number: item.invoice_number || '',
        sale_date: item.sale_date ? fmtDate(item.sale_date) : '',
        total_amount: item.total_amount || 0,
        paid_amount: item.paid_amount || 0,
        remaining_amount: item.remaining_amount || 0,
        payment_status: item.payment_status || ''
      }));
      
      await generateReportExcel('sales_ledger', p('report_title'), {
        sheetName: p('sheet_name'),
      });
      
    } catch (error) {
      console.error('Excel export error:', error);
      alert(p('error_excel'));
    } finally {
      setIsGeneratingExcel(false);
    }
  };

  const statusOptions = useMemo(() => [
    { value: 'pending', label: p('status_pending'), icon: Clock, style: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
    { value: 'partial', label: p('status_partial'), icon: AlertCircle, style: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
    { value: 'paid', label: p('status_paid'), icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: 'cancelled', label: p('status_cancelled'), icon: XCircle, style: 'bg-red-500/20 text-red-400 border-red-500/30' }
  ], [t]);

  const handleStatusChange = async (recordId, newStatus) => {
    try {
      await localApiAdapter.put(`/ventes/livre-des-ventes/${recordId}`, { payment_status: newStatus });
      await loadSales();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const getPaymentProgress = (paid, total) => {
    const paidAmount = parseFloat(paid) || 0;
    const totalAmount = parseFloat(total) || 0;
    
    if (totalAmount === 0) return 0;
    const percentage = ((paidAmount / totalAmount) * 100).toFixed(0);
    return percentage;
  };

  const getPaymentProgressBar = (paid, total) => {
    const percentage = parseFloat(getPaymentProgress(paid, total));
    
    let colorClass = 'bg-red-500';
    if (percentage === 100) colorClass = 'bg-green-500';
    else if (percentage >= 50) colorClass = 'bg-yellow-500';
    
    return (
      <div className="w-full bg-white/10 h-2 rounded-full overflow-hidden">
        <div className={`h-2 rounded-full ${colorClass} transition-all`} style={{ width: `${percentage}%` }}></div>
      </div>
    );
  };

  const getOverdueBadge = (dueDate, paymentStatus) => {
    if (paymentStatus === 'paid') return null;
    if (!dueDate) return null;
    
    const today = new Date();
    const due = new Date(dueDate);
    
    if (due < today) {
      const daysOverdue = Math.floor((today - due) / (1000 * 60 * 60 * 24));
      return <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-500/20 text-red-400 border border-red-500/30 flex items-center gap-1">
        <AlertTriangle size={12} />
{p('overdue_days', { days: daysOverdue })}
      </span>;
    }
    
    return null;
  };

  const listStats = useMemo(
    () => mapVentesStatCards([
      { key: 'total', label: p('stat_total_sales'), value: stats.total, icon: BookOpen, variant: 'copper' },
      { key: 'pending', label: p('stat_pending'), value: stats.pending, icon: Clock, variant: 'forest' },
      { key: 'paid', label: p('stat_paid'), value: stats.paid, icon: CheckCircle, variant: 'copper' },
      { key: 'total_amount', label: p('stat_total_amount'), value: formatBrandMoney(stats.totalAmount), icon: DollarSign, variant: 'forest' },
    ]),
    [stats, p],
  );

  const advancedOptionsContent = (
    <div className="space-y-6 pt-6">
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
        <div className="flex space-x-1 overflow-x-auto table-scroll">
          {mainTabs.map((tab) => (
            <button key={tab.id} type="button" onClick={() => { setMainViewMode(tab.id); setQuickFilter('all'); setActiveDistributionFilter(null); }} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
              <tab.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
            </button>
          ))}
        </div>
      </div>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold text-white">{distributionInfo.title}</h3>
          {activeDistributionFilter && (
            <button type="button" onClick={() => setActiveDistributionFilter(null)} className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1">
              <X size={14} />{p('reset_filter')}
            </button>
          )}
        </div>
        <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '3', 'lg:grid-cols-4')}>{distributionInfo.data.map((item) => (
            <button key={item.filterKey || item.name} type="button" onClick={() => setActiveDistributionFilter(activeDistributionFilter === (item.filterKey || item.name) ? null : (item.filterKey || item.name))} className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${activeDistributionFilter === (item.filterKey || item.name) ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20' : 'border-white/10 hover:border-white/20 hover:bg-white/10'}`} title={p('filter_click_title', { name: item.name })}>
              <div className="flex justify-between items-center mb-2">
                <h4 className={`font-medium ${activeDistributionFilter === (item.filterKey || item.name) ? 'text-white' : 'text-white/80'}`}>{item.name}</h4>
                <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>{item.count}</div>
              </div>
              <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                <div className={`h-3 rounded-full ${item.color} transition-all`} style={{ width: `${(() => { const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0); return total > 0 ? (item.count / total * 100).toFixed(1) : 0; })()}%` }} />
              </div>
            </button>
          ))}
        </div>
      </motion.div>
    </div>
  );

  return (
    <>
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
      <VentesConfigNotice
        message={!ventesConfig.enableSalesLedger ? p('notice_sales_ledger_disabled', { defaultValue: 'دفتر المبيعات معطّل — فعّله من إعدادات المبيعات ← دفتر المبيعات.' }) : null}
      />
      <VentesListPageChrome
        t={t}
        p={p}
        qawaem={qawaem}
        isDashboardLightMode={isDashboardLightMode}
        title={p('title')}
        subtitle={p('subtitle')}
        icon={BookOpen}
        listStats={listStats}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        filterActions={buildVentesBulkDeleteFilterAction({
          selectedCount: selectedRecords.length,
          onDelete: handleBulkDelete,
          label: p('bulk_delete_confirm', { count: selectedRecords.length }),
        })}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder={p('search_placeholder', { defaultValue: t('common.search') })}
        onCreate={viewType === 'manual' && ventesConfig.enableSalesLedger ? openCreateSaleModal : undefined}
        createLabel={p('new_sale')}
        importExportState={{ showImportDropdown, setShowImportDropdown, showExportDropdown, setShowExportDropdown, importDropdownRef, exportDropdownRef }}
        importExportHandlers={{ onExportCSV: handleExport, onExportExcel: handleExportExcel, onExportPDF: handleExportPDF }}
        isGeneratingPDF={isGeneratingPDF}
        showAdvancedOptions={showAdvancedOptions}
        onToggleAdvancedOptions={() => setShowAdvancedOptions(!showAdvancedOptions)}
        advancedOptionsLabel={p('advanced_options')}
        advancedOptionsContent={advancedOptionsContent}
        beforeTable={viewTypeToggle}
      >
        {viewMode === 'table' && (
          <div className={facturationTableScrollClass(qawaem)}>
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <th className={mergeStickySelectClass('px-3 py-3 text-center align-middle')}>
                    <div className="flex justify-center">
                      <QawaemTableCheckbox
                        size="lg"
                        checked={allRowsSelected}
                        indeterminate={someRowsSelected}
                        onChange={handleSelectAll}
                        title={allRowsSelected ? p('deselect_all', { defaultValue: t('common.deselect_all') }) : p('select_all', { defaultValue: t('common.select_all') })}
                      />
                    </div>
                  </th>
                  
                  <TableColumnHeaderFilter columnKey="sale_number" label={p('col_sale_number')} value={filters.sale_number || ''} onChange={(v) => setFilter('sale_number', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName={thFilterClass} />
                  {showCol('customer_name') ? (
                    <TableColumnHeaderFilter columnKey="customer_name" label={p('col_customer')} value={filters.customer_name || ''} onChange={(v) => setFilter('customer_name', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName={thFilterClass} />
                  ) : null}
                  {showCol('sale_date') ? (
                    <TableColumnHeaderFilter columnKey="sale_date" label={p('col_date')} value={filters.sale_date || ''} onChange={(v) => setFilter('sale_date', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName={thFilterClass} />
                  ) : null}
                  {showCol('total_amount') ? (
                    <TableColumnHeaderFilter columnKey="total_amount" label={p('col_amount')} value={filters.total_amount || ''} onChange={(v) => setFilter('total_amount', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName={thFilterClass} />
                  ) : null}
                  {showCol('payment_method') ? (
                    <TableColumnHeaderFilter columnKey="payment_method" label={p('col_payment')} value={filters.payment_method || ''} onChange={(v) => setFilter('payment_method', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName={thFilterClass} />
                  ) : null}
                  {showCol('payment_status') ? (
                    <TableColumnHeaderFilter columnKey="payment_status" label={p('status_label')} kind="select" options={paymentStatusFilterOptions} value={filters.payment_status || ''} onChange={(v) => setFilter('payment_status', v)} align="center" editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName={thFilterClass} />
                  ) : null}
                  <QawaemTableHeaderActionsCell
                    label={p('col_actions')}
                    columns={qawaemColumnDefs}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumnDefs.length}
                    thClassName="px-6 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle"
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {displayedSales.length > 0 ? (
                  displayedSales.map((record, index) => (
                    <tr key={record.id} className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''}`}>
                      <td className={mergeStickySelectClass('px-3 py-4 text-center align-middle')}>
                        <div className="flex justify-center">
                          <QawaemTableCheckbox
                            checked={selectedRecords.includes(record.id)}
                            onChange={() => handleSelectRecord(record.id)}
                          />
                        </div>
                      </td>
                      
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold flex items-center gap-2">
                            <ShoppingCart size={16} className="text-blue-400" />
                            {record.sale_number}
                          </span>
                          {record.invoice_number && <span className="text-white/50 text-xs mt-0.5">{p('invoice_prefix')} {record.invoice_number}</span>}
                        </div>
                      </td>
                      {showCol('customer_name') ? (
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <span className="text-white font-semibold">{record.customer_name || p('na')}</span>
                            {record.customer_code && <span className="text-white/50 text-xs mt-0.5">{p('card_label_code')} {record.customer_code}</span>}
                          </div>
                        </td>
                      ) : null}
                      {showCol('sale_date') ? (
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <div className="flex items-center gap-1.5 text-white font-semibold">
                              <Clock size={14} className="text-white/50" />
                              {fmtDate(record.sale_date)}
                            </div>
                            {record.due_date && (
                              <span className="text-white/50 text-xs mt-0.5">
                                {p('due_prefix')} {fmtDate(record.due_date)}
                              </span>
                            )}
                          </div>
                        </td>
                      ) : null}
                      {showCol('total_amount') ? (
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <span className="text-white font-semibold">{formatBrandMoney(record.total_amount || 0)}</span>
                            <span className="text-white/50 text-xs mt-0.5">
                              {p('items_count', { count: record.product_count || 0 })}
                            </span>
                          </div>
                        </td>
                      ) : null}
                      {showCol('payment_method') ? (
                        <td className="px-6 py-4">
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-green-400">{formatBrandMoney(record.paid_amount || 0)}</span>
                              <span className="text-white/50">{getPaymentProgress(record.paid_amount, record.total_amount)}%</span>
                            </div>
                            {getPaymentProgressBar(record.paid_amount, record.total_amount)}
                            <span className="text-red-400 text-xs">{p('remaining_prefix')} {formatBrandMoney(record.remaining_amount || 0)}</span>
                          </div>
                        </td>
                      ) : null}
                      {showCol('payment_status') ? (
                        <td className="px-6 py-4 text-center">
                          <div className="flex flex-col items-center gap-1">
                            <QawaemClickableStatusBadge
                              status={record.payment_status}
                              recordId={record.id}
                              options={statusOptions}
                              onChange={handleStatusChange}
                              title={p('status_change_title')}
                            />
                            {getOverdueBadge(record.due_date, record.payment_status)}
                          </div>
                        </td>
                      ) : null}
                      <td className="px-6 py-4">
                        <QawaemErpRowActions
                          onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                          onEdit={() => handleEdit(record)}
                          onDelete={() => handleDelete(record.id)}
                          viewTitle={t('common.view')}
                          editTitle={t('common.edit')}
                          deleteTitle={t('common.delete')}
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={tableColSpan} className="text-center py-12 text-white/60"><ShoppingCart size={48} className="mx-auto mb-4 text-white/30" /><p className="text-lg">{p('empty_sales')}</p></td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {displayedSales.length > 0 ? (
              displayedSales.map((record, index) => (
                <motion.div
                  key={record.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-4 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2">
                          <ShoppingCart size={20} className="text-blue-400" />
                          {record.sale_number}
                        </h3>
                        <QawaemClickableStatusBadge
                          status={record.payment_status}
                          recordId={record.id}
                          options={statusOptions}
                          onChange={handleStatusChange}
                          title={p('status_change_title')}
                        />
                        {getOverdueBadge(record.due_date, record.payment_status)}
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_customer')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('card_label_name')}</span> {record.customer_name || p('na')}</div>
                            <div><span className="font-medium">{p('card_label_code')}</span> {record.customer_code || 'N/A'}</div>
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_dates')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            {record.sale_date && <div><span className="font-medium">{p('card_sale_label')}</span> {fmtDate(record.sale_date)}</div>}
                            {record.invoice_date && <div><span className="font-medium">{p('card_invoice_label')}</span> {fmtDate(record.invoice_date)}</div>}
                            {record.due_date && <div><span className="font-medium">{p('card_due_label')}</span> {fmtDate(record.due_date)}</div>}
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_amounts')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('card_label_total')}</span> {formatBrandMoney(record.total_amount || 0)}</div>
                            <div className="text-green-400"><span className="font-medium">{p('card_label_paid')}</span> {formatBrandMoney(record.paid_amount || 0)}</div>
                            <div className="text-red-400"><span className="font-medium">{p('card_label_remaining')}</span> {formatBrandMoney(record.remaining_amount || 0)}</div>
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_payment')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div>{getPaymentProgressBar(record.paid_amount, record.total_amount)}</div>
                            <div><span className="font-medium">{p('card_label_progress')}</span> {getPaymentProgress(record.paid_amount, record.total_amount)}%</div>
                            {record.payment_method && <div><span className="font-medium">{p('card_label_method')}</span> {getPaymentMethodLabel(record.payment_method)}</div>}
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_details')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('card_label_items')}</span> {record.product_count || 0}</div>
                            {record.warehouse_name && <div><span className="font-medium">{p('card_label_warehouse')}</span> {record.warehouse_name}</div>}
                            <p className="text-xs mt-2">{record.notes || p('no_notes')}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <QawaemErpRowActions
                      compact
                      size={14}
                      onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                      onEdit={() => handleEdit(record)}
                      onDelete={() => handleDelete(record.id)}
                      viewTitle={t('common.view')}
                      editTitle={t('common.edit')}
                      deleteTitle={t('common.delete')}
                    />
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <ShoppingCart size={48} className="mx-auto mb-4 text-white/30" />
                <p className="text-white/60 text-lg">{p('empty_sales')}</p>
              </div>
            )}
          </div>
        )}
      </VentesListPageChrome>

      {showViewModal && viewingRecord ? (
      <QawaemErpViewModal
        open
        onClose={() => setShowViewModal(false)}
        title={p('view_sale_details')}
        icon={Eye}
        maxWidth="1100px"
        testId="livre-ventes-view-modal"
      >
              <div className="space-y-4">
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <ShoppingCart size={18} className="text-blue-400" />
                    {p('section_identification')}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('col_sale_number')}</p>
                      <p className="text-white font-medium text-lg">{viewingRecord.sale_number}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('col_invoice_number')}</p>
                      <p className="text-white font-medium">{viewingRecord.invoice_number || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('col_payment_status')}</p>
                      <div className="flex">
                        <QawaemClickableStatusBadge
                          status={viewingRecord.payment_status}
                          recordId={viewingRecord.id}
                          options={statusOptions}
                          onChange={handleStatusChange}
                          title={p('status_change_title')}
                        />
                      </div>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('field_payment_method')}</p>
                      <p className="text-white font-medium">{getPaymentMethodLabel(viewingRecord.payment_method) || p('na')}</p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <User size={18} className="text-green-400" />
                      {p('section_customer')}
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('card_label_name').replace(':', '')}</p>
                        <p className="text-white font-medium">{viewingRecord.customer_name || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('card_label_code').replace(':', '')}</p>
                        <p className="text-white font-medium">{viewingRecord.customer_code || 'N/A'}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <DollarSign size={18} className="text-purple-400" />
                      {p('section_amounts')}
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('card_label_total').replace(':', '')}</p>
                        <p className="text-white font-medium text-lg">{formatBrandMoney(viewingRecord.total_amount || 0)}</p>
                      </div>
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('col_paid')}</p>
                        <p className="text-green-400 font-medium">{formatBrandMoney(viewingRecord.paid_amount || 0)}</p>
                      </div>
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('col_remaining')}</p>
                        <p className="text-red-400 font-medium">{formatBrandMoney(viewingRecord.remaining_amount || 0)}</p>
                      </div>
                      <div className="pt-2">
                        {getPaymentProgressBar(viewingRecord.paid_amount, viewingRecord.total_amount)}
                        <p className="text-white/60 text-xs mt-1">{p('card_label_progress')} {getPaymentProgress(viewingRecord.paid_amount, viewingRecord.total_amount)}%</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <FileText size={18} className="text-yellow-400" />
                    {p('section_notes')}
                  </h3>
                  <p className="text-white/80">{viewingRecord.notes || p('no_notes')}</p>
                </div>
              </div>
      </QawaemErpViewModal>
      ) : null}

      <QawaemErpCrudModal
        open={showModal}
        onClose={resetForm}
        title={editingRecord ? p('modal_edit_sale') : p('modal_new_sale')}
        icon={BookOpen}
        showSaveButton
        formId="sale-form"
        saveLabel={editingRecord ? p('update') : p('create')}
        testId="livre-ventes-crud-modal"
        tabs={saleFormTabs}
        activeTab={formTab}
        onTabChange={setFormTab}
        tabsAriaLabel={editingRecord ? p('modal_edit_sale') : p('modal_new_sale')}
      >
        <form id="sale-form" onSubmit={handleSubmit}>
          {formTab === 'general' && (
            <div className="space-y-6">
              <section>
                <h4 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${isDashboardLightMode ? 'text-gray-800' : 'text-white/90'}`}>
                  <ShoppingCart size={16} className="text-blue-400" />
                  {p('section_identification')}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('col_sale_number')} <span className="qawaem-required-mark">*</span></label>
                    <input
                      type="text"
                      required
                      value={formData.sale_number || ''}
                      onChange={(e) => setFormData({ ...formData, sale_number: e.target.value })}
                      className={VENTES_REF_INPUT_CLASS}
                      dir="ltr"
                    />
                  </div>
                  <div>
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_invoice_number')}</label>
                    <input type="text" value={formData.invoice_number} onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('ph_invoice')} />
                  </div>
                  <div>
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_payment_status')}</label>
                    <CustomSelect value={formData.payment_status} onChange={(value) => setFormData({ ...formData, payment_status: value })} options={statusOptions.map(s => ({ value: s.value, label: s.label, icon: <s.icon size={16} /> }))} placeholder={p('status_label')} />
                  </div>
                  <div>
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_sale_date')}</label>
                    <CustomDatePicker value={formData.sale_date} onChange={(date) => setFormData({ ...formData, sale_date: date })} placeholder={p('ph_sale_date')} />
                  </div>
                  <div>
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_invoice_date')}</label>
                    <CustomDatePicker value={formData.invoice_date} onChange={(date) => setFormData({ ...formData, invoice_date: date })} placeholder={p('ph_invoice_date')} />
                  </div>
                  <div>
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_due_date')}</label>
                    <CustomDatePicker value={formData.due_date} onChange={(date) => setFormData({ ...formData, due_date: date })} placeholder={p('ph_due_date')} minDate={formData.sale_date} />
                  </div>
                  <div>
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_payment_method')}</label>
                    <CustomSelect value={formData.payment_method} onChange={(value) => setFormData({ ...formData, payment_method: value })} options={paymentMethodOptions} placeholder={p('select_placeholder')} />
                  </div>
                </div>
              </section>
              <section className="pt-2 border-t border-white/10">
                <h4 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${isDashboardLightMode ? 'text-gray-800' : 'text-white/90'}`}>
                  <User size={16} className="text-green-400" />
                  {p('section_customer')}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">
                      {p('field_customer')}
                      {ventesConfig.requireCustomer !== false ? <span className="qawaem-required-mark"> *</span> : null}
                    </label>
                    <CustomSelect
                      value={formData.customer_id ? String(formData.customer_id) : ''}
                      onChange={(clientId) => {
                        if (!clientId) {
                          setFormData({
                            ...formData,
                            customer_id: '',
                            customer_name: '',
                            customer_code: '',
                          });
                          return;
                        }
                        const selectedCustomer = availableCustomers.find((c) => String(c.id) === String(clientId));
                        setFormData({
                          ...formData,
                          customer_id: String(clientId),
                          customer_name: selectedCustomer?.company_name || selectedCustomer?.name || '',
                          customer_code: selectedCustomer?.code || selectedCustomer?.client_code || '',
                        });
                      }}
                      options={availableCustomers.map((c) => ({
                        value: String(c.id),
                        label: `${c.company_name || c.name || p('col_customer')}${c.contact_person ? ` — ${c.contact_person}` : ''}${c.phone1 ? ` — ${c.phone1}` : ''}`,
                      }))}
                      placeholder={p('ph_select_customer')}
                      disabled={loadingCustomers}
                      searchable
                      required={ventesConfig.requireCustomer !== false}
                    />
                  </div>
                  <div>
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_customer_code')}</label>
                    <input type="text" value={formData.customer_code} readOnly className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-2.5 text-white/70 cursor-not-allowed" placeholder={p('ph_auto_generated')} />
                  </div>
                  <div>
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_warehouse')}</label>
                    <CustomSelect
                      value={formData.warehouse_name}
                      onChange={(value) => {
                        const selectedWarehouse = availableWarehouses.find(w => w.name === value);
                        setFormData({
                          ...formData,
                          warehouse_name: value,
                          warehouse_id: selectedWarehouse?.id || '',
                        });
                      }}
                      options={availableWarehouses.map(w => ({ value: w.name, label: w.name }))}
                      placeholder={p('ph_select_warehouse')}
                      searchable
                      creatable
                    />
                  </div>
                </div>
              </section>
            </div>
          )}
          {formTab === 'amounts' && (
            <div className="space-y-6">
              <section>
                <h4 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${isDashboardLightMode ? 'text-gray-800' : 'text-white/90'}`}>
                  <DollarSign size={16} className="text-purple-400" />
                  {p('section_amounts')}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_total_amount')}</label>
                    <CustomNumberInput
                      value={formData.total_amount}
                      onChange={(value) => {
                        setFormData({
                          ...formData,
                          total_amount: value,
                          remaining_amount: (parseFloat(value) || 0) - (parseFloat(formData.paid_amount) || 0),
                        });
                      }}
                      placeholder={p('placeholder_decimal')}
                      step={0.01}
                      min={0}
                    />
                  </div>
                  <div>
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_paid_amount')}</label>
                    <CustomNumberInput
                      value={formData.paid_amount}
                      onChange={(value) => {
                        setFormData({
                          ...formData,
                          paid_amount: value,
                          remaining_amount: (parseFloat(formData.total_amount) || 0) - (parseFloat(value) || 0),
                        });
                      }}
                      placeholder={p('placeholder_decimal')}
                      step={0.01}
                      min={0}
                    />
                  </div>
                  <div>
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_remaining_amount')}</label>
                    <CustomNumberInput value={formData.remaining_amount} onChange={(value) => setFormData({ ...formData, remaining_amount: value })} placeholder={p('placeholder_decimal')} step={0.01} min={0} />
                  </div>
                  {ventesConfig.showTaxBreakdown !== false ? (
                    <div>
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_tax')}</label>
                      <CustomNumberInput value={formData.tax_amount} onChange={(value) => setFormData({ ...formData, tax_amount: value })} placeholder={p('placeholder_decimal')} step={0.01} min={0} />
                    </div>
                  ) : null}
                  <div>
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_discount')}</label>
                    <CustomNumberInput value={formData.discount_amount} onChange={(value) => setFormData({ ...formData, discount_amount: value })} placeholder={p('placeholder_decimal')} step={0.01} min={0} />
                  </div>
                  <div>
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_product_count')}</label>
                    <CustomNumberInput value={formData.product_count} onChange={(value) => setFormData({ ...formData, product_count: value })} placeholder={p('placeholder_zero')} step={1} min={0} />
                  </div>
                </div>
              </section>
            </div>
          )}
          {formTab === 'notes' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-3">
                <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('section_notes')}</label>
                <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={4} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('ph_notes')} />
              </div>
            </div>
          )}
        </form>
      </QawaemErpCrudModal>
    </>
  );
};

export default LivreDesVentesPage;

